import { useCallback, useMemo, useRef } from 'react';
import { PerformanceFacade, triggerNotificationsFailedToLoad, triggerNotificationsLoaded, useCreateFireAnalyticsFromTrigger } from '@atlassian/notification-common';
import { NotificationExperienceMarkKeys, useExperienceTracker } from '@atlassian/notification-common/experience-tracking';
import { useFetchService } from '../use-fetch-service';
import fetchNotifications from '../utils/inbox-client';
import { RequestExpand } from '../utils/inbox-client/types';
import { handleWithContentExperienceTracking } from './utils';
var performance = new PerformanceFacade(window.performance);
export var useInboxClient = function useInboxClient(_ref) {
  var locale = _ref.locale,
    filters = _ref.filters,
    expand = _ref.expand;
  var experienceTracker = useExperienceTracker();
  var firstLoad = useRef(true);
  var fireNotificationsLoaded = useCreateFireAnalyticsFromTrigger(triggerNotificationsLoaded);
  var fireNotificationsFailedToLoad = useCreateFireAnalyticsFromTrigger(triggerNotificationsFailedToLoad);
  var withContent = expand === RequestExpand.CONTENT;
  var notificationsRequest = useCallback(function (request) {
    var start = performance.now();
    if (withContent) {
      experienceTracker.markStart(NotificationExperienceMarkKeys.API_WITH_CONTENT);
    } else {
      experienceTracker.markStart(NotificationExperienceMarkKeys.API_WITHOUT_CONTENT);
    }
    return fetchNotifications(filters.categoryFilter, filters.readStateFilter, filters.productFilter, expand, locale, request.continuationToken, request.limit).then(function (r) {
      var duration = Math.trunc(performance.now() - start);
      fireNotificationsLoaded(firstLoad.current, withContent, duration, request.limit);
      if (firstLoad.current) {
        if (withContent) {
          handleWithContentExperienceTracking(r.notifications, experienceTracker);
          experienceTracker.markEnd(NotificationExperienceMarkKeys.API_WITH_CONTENT);
        }

        // The API returns content.body.items for share notifications
        // even if we don't request it.
        //
        // This causes issues with our experience tracking as it causes us to render ExperienceSuccess
        // before we start the experiences in handleWithContentExperienceTracking that is called
        // when the withContent completes.
        //
        // For these reasons we clear the body.items for the first load for the 'withoutContent' request
        else {
          experienceTracker.markEnd(NotificationExperienceMarkKeys.API_WITHOUT_CONTENT);
          r.notifications.forEach(function (notification) {
            var _notification$content;
            if ((_notification$content = notification.content) !== null && _notification$content !== void 0 && (_notification$content = _notification$content.body) !== null && _notification$content !== void 0 && _notification$content.items && notification.content.body.items.length > 0) {
              notification.content.body.items = [];
            }
          });
        }
      }
      firstLoad.current = false;
      return r;
    }).catch(function (e) {
      fireNotificationsFailedToLoad(firstLoad.current, withContent, request.limit, e.code);
      firstLoad.current = false;
      throw e;
    });
  }, [filters.categoryFilter, filters.readStateFilter, filters.productFilter, expand, locale, withContent, experienceTracker, fireNotificationsLoaded, fireNotificationsFailedToLoad]);

  // Memoise initialData otherwise it causes an fetchData to be recreated each time the hook runs.
  var initialData = useMemo(function () {
    return {
      args: {
        filters: filters,
        limit: 8
      },
      data: {
        notifications: []
      }
    };
  }, [filters]);
  var _useFetchService = useFetchService(notificationsRequest, initialData, 'inbox-client'),
    loadingState = _useFetchService.loadingState,
    error = _useFetchService.error,
    data = _useFetchService.data,
    fetchData = _useFetchService.fetchData,
    lastCallTimestamp = _useFetchService.lastCallTimestamp;
  return {
    loadingState: loadingState,
    error: error,
    data: data,
    lastCallTimestamp: lastCallTimestamp,
    fetchData: fetchData
  };
};